import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';

interface SubscriptionFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SubscriptionFormData) => void;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
  editData?: SubscriptionFormData & { id?: number };
}

export interface SubscriptionFormData {
  id?: number;
  service_name: string;
  price: number;
  cycle: 'monthly' | 'semiannual' | 'annual';
  initial_date: string;
  next_payment_date?: string;
  payment_method?: string;
  category_id?: number;
  memo?: string;
}

// うるう年判定のヘルパー関数
const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

// 次回支払日を自動計算する関数
export const calculateNextPaymentDate = (initialDate: string, cycle: string): string => {
  if (!initialDate) return '';
  
  const initial = new Date(initialDate);
  
  // 無効な日付をチェック
  if (isNaN(initial.getTime())) return '';
  
  const next = new Date(initial);
  
  switch (cycle) {
    case 'monthly':
      // 月末日の場合の特別処理
      const originalDay = initial.getDate();
      next.setMonth(next.getMonth() + 1);
      
      // 元の日が新しい月に存在しない場合（例：1/31 -> 2/28）
      if (next.getDate() !== originalDay) {
        next.setDate(0); // 前月の最終日に設定
      }
      break;
      
    case 'semiannual':
      next.setMonth(next.getMonth() + 6);
      break;
      
    case 'annual':
      const originalAnnualMonth = initial.getMonth();
      const originalAnnualDay = initial.getDate();
      const targetYear = initial.getFullYear() + 1;
      
      // 新しい年で安全に日付を作成
      // うるう年の2月29日から通常年への変換の場合、2月28日に調整
      if (originalAnnualMonth === 1 && originalAnnualDay === 29 && !isLeapYear(targetYear)) {
        next.setFullYear(targetYear, originalAnnualMonth, 28);
      } else {
        next.setFullYear(targetYear, originalAnnualMonth, originalAnnualDay);
      }
      break;
  }
  
  return next.toISOString().split('T')[0];
};

const SubscriptionFormModal: React.FC<SubscriptionFormModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  mode = 'create',
  editData
}) => {
  const [formData, setFormData] = useState<SubscriptionFormData>({
    service_name: '',
    price: 0,
    cycle: 'monthly',
    initial_date: '',
    payment_method: '',
    memo: ''
  });

  const [nextPaymentPreview, setNextPaymentPreview] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 編集データが変更されたときにフォームを初期化
  React.useEffect(() => {
    if (mode === 'edit' && editData) {
      setFormData({
        service_name: editData.service_name,
        price: editData.price,
        cycle: editData.cycle,
        initial_date: editData.initial_date,
        next_payment_date: editData.next_payment_date,
        payment_method: editData.payment_method || '',
        category_id: editData.category_id,
        memo: editData.memo || ''
      });
      
      // 次回支払日のプレビューも設定
      if (editData.next_payment_date) {
        setNextPaymentPreview(editData.next_payment_date);
      } else {
        const preview = calculateNextPaymentDate(editData.initial_date, editData.cycle);
        setNextPaymentPreview(preview);
      }
    } else {
      // 新規作成モードの場合はフォームをリセット
      setFormData({
        service_name: '',
        price: 0,
        cycle: 'monthly',
        initial_date: '',
        payment_method: '',
        memo: ''
      });
      setNextPaymentPreview('');
    }
    setErrors({});
  }, [mode, editData, open]);

  // バリデーション関数
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // サービス名の必須チェック
    if (!formData.service_name.trim()) {
      newErrors.service_name = 'サービス名は必須です';
    }

    // 価格のバリデーション
    if (formData.price <= 0) {
      newErrors.price = '価格は1円以上を入力してください';
    }
    if (!Number.isInteger(formData.price)) {
      newErrors.price = '価格は整数で入力してください';
    }

    // 初回支払日の必須チェック
    if (!formData.initial_date) {
      newErrors.initial_date = '初回支払日は必須です';
    } else {
      // 日付の妥当性チェック
      const date = new Date(formData.initial_date);
      if (isNaN(date.getTime())) {
        newErrors.initial_date = '有効な日付を入力してください';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フォームデータ更新時に次回支払日をプレビュー
  const handleInputChange = (field: keyof SubscriptionFormData, value: string | number) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);

    // 該当フィールドのエラーをクリア
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // 初回支払日またはサイクルが変更された時に次回支払日を自動計算
    if (field === 'initial_date' || field === 'cycle') {
      const preview = calculateNextPaymentDate(
        field === 'initial_date' ? value as string : updatedData.initial_date,
        field === 'cycle' ? value as string : updatedData.cycle
      );
      setNextPaymentPreview(preview);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション実行
    if (!validateForm()) {
      return; // バリデーションエラーがある場合は送信しない
    }
    
    // 次回支払日が設定されていない場合は自動計算した値を使用
    const submissionData = {
      ...formData,
      next_payment_date: formData.next_payment_date || nextPaymentPreview,
      // 編集モードの場合はIDも含める
      ...(mode === 'edit' && editData?.id && { id: editData.id })
    };
    
    onSubmit(submissionData);
    
    // 新規作成モードの場合のみフォームをリセット
    if (mode === 'create') {
      setFormData({
        service_name: '',
        price: 0,
        cycle: 'monthly',
        initial_date: '',
        payment_method: '',
        memo: ''
      });
      setNextPaymentPreview('');
      setErrors({});
    }
    onOpenChange(false);
  };

  const cycleOptions = [
    { value: 'monthly', label: '毎月' },
    { value: 'semiannual', label: '半年' },
    { value: 'annual', label: '年間' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'サブスクリプションを編集' : '新しいサブスクリプションを追加'}
          </DialogTitle>
          <DialogClose onClick={() => onOpenChange(false)} />
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-4">
          {/* サービス名 */}
          <div className="space-y-2">
            <Label htmlFor="service_name">サービス名 *</Label>
            <Input
              id="service_name"
              type="text"
              value={formData.service_name}
              onChange={(e) => handleInputChange('service_name', e.target.value)}
              placeholder="例: Netflix"
              required
              className={`text-base sm:text-sm ${errors.service_name ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {errors.service_name && (
              <p className="text-sm text-red-600">{errors.service_name}</p>
            )}
          </div>

          {/* 価格 */}
          <div className="space-y-2">
            <Label htmlFor="price">価格 (円) *</Label>
            <Input
              id="price"
              type="number"
              min="1"
              value={formData.price}
              onChange={(e) => handleInputChange('price', parseInt(e.target.value) || 0)}
              placeholder="例: 1490"
              required
              className={`text-base sm:text-sm ${errors.price ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {errors.price && (
              <p className="text-sm text-red-600">{errors.price}</p>
            )}
          </div>

          {/* 支払いサイクル */}
          <div className="space-y-2">
            <Label htmlFor="cycle">支払いサイクル *</Label>
            <select
              id="cycle"
              value={formData.cycle}
              onChange={(e) => handleInputChange('cycle', e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 bg-white text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-blue-400"
              required
            >
              {cycleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* 初回支払日 */}
          <div className="space-y-2">
            <Label htmlFor="initial_date">初回支払日 *</Label>
            <Input
              id="initial_date"
              type="date"
              value={formData.initial_date}
              onChange={(e) => handleInputChange('initial_date', e.target.value)}
              required
              className={`text-base sm:text-sm ${errors.initial_date ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {errors.initial_date && (
              <p className="text-sm text-red-600">{errors.initial_date}</p>
            )}
          </div>

          {/* 次回支払日プレビュー */}
          {nextPaymentPreview && (
            <Card className="p-3 bg-blue-50 border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>次回支払日 (自動計算):</strong> {nextPaymentPreview}
              </div>
            </Card>
          )}

          {/* 支払い方法 */}
          <div className="space-y-2">
            <Label htmlFor="payment_method">支払い方法</Label>
            <Input
              id="payment_method"
              type="text"
              value={formData.payment_method}
              onChange={(e) => handleInputChange('payment_method', e.target.value)}
              placeholder="例: クレジットカード, PayPal"
              className="text-base sm:text-sm"
            />
          </div>

          {/* メモ */}
          <div className="space-y-2">
            <Label htmlFor="memo">メモ</Label>
            <textarea
              id="memo"
              value={formData.memo}
              onChange={(e) => handleInputChange('memo', e.target.value)}
              placeholder="任意のメモを入力..."
              className="w-full px-3 py-2.5 sm:py-2 text-base sm:text-sm border border-gray-300 bg-white text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-none touch-manipulation dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400 dark:focus:ring-blue-400"
              rows={3}
            />
          </div>

          {/* 送信ボタン */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="w-full sm:w-auto py-2.5 sm:py-2 text-base sm:text-sm font-medium"
            >
              キャンセル
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full sm:w-auto py-2.5 sm:py-2 text-base sm:text-sm font-medium"
            >
              {isLoading 
                ? (mode === 'edit' ? '更新中...' : '追加中...')
                : (mode === 'edit' ? '更新' : '追加')
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionFormModal;
